import React from 'react';
import { Box, Typography } from '@mui/material';

import { ORDER_STATUS_META } from '../../constants/orderStatus';
import { PAYMENT_STATUS_META } from '../../constants/paymentStatus';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * 🔴 DEUX FAMILLES DE STATUT, ET ELLES SONT BIEN DISTINCTES.
 *
 * `orderStatus.js` décrit l'avancement LOGISTIQUE d'une commande,
 * `paymentStatus.js` son avancement FINANCIER, dérivé et jamais stocké. Le
 * fichier des constantes le dit déjà : « domaine DISTINCT », et « `pending`
 * ici n'a AUCUN rapport avec un statut de commande ».
 *
 * ⚠️ C'EST CETTE DUALITÉ QUI A LAISSÉ PASSER UN DÉFAUT. Le lot RU2 a converti
 * la colonne « Statut » en pastille et laissé la colonne « Statut de paiement »
 * en `<Chip>` — **deux colonnes ADJACENTES, deux traitements, dans le même
 * tableau**. La garde ne l'a pas vu parce qu'elle ne surveillait qu'un seul
 * jeu de constantes : `ORDER_STATUS_META`. Une garde qui connaît une famille
 * ne protège pas l'autre.
 *
 * Un seul composant peint donc les deux, et la garde couvre les deux jeux.
 */
const FAMILLES = {
  commande: ORDER_STATUS_META,
  paiement: PAYMENT_STATUS_META,
};

/**
 * Le statut — un point de couleur et un texte neutre. Deux familles.
 *
 * ── LES QUATRE CONSOMMATEURS, RELEVÉS AVANT ÉCRITURE ────────────────────────
 * Ils ne rendaient PAS la même chose, et unifier a donc **changé trois
 * écrans**, pas seulement habillé un composant :
 *
 *   1. `OrderDetailView` — `<Chip color={colorKey} size="small">`
 *   2. `OrderReception`  — **le même code, recopié à l'identique**
 *   3. `PurchaseOrders`  — un `<Chip>` construit via `getStatusOptions(t)`,
 *      donc un chemin différent pour un rendu identique
 *   4. `Suppliers`       — 🔴 **cassé**, voir ci-dessous
 *
 * 🔴 LE QUATRIÈME ÉTAIT UN DÉFAUT, ET IL A ÉTÉ INTRODUIT PAR LE LOT PRÉCÉDENT.
 * `Suppliers` rendait le statut dans un `<span style={{ color: … }}>` — un
 * attribut `style` NATIF, pas une prop `sx`. En corrigeant les couleurs en dur
 * au lot RU1, ses valeurs (`'#1976d2'`, `'green'`, `'red'`, `'#888'`) ont été
 * remplacées par des jetons MUI (`'info.main'`, `'success.main'`…). **Or un
 * jeton de thème n'est pas une couleur CSS** : dans un `style` natif il est
 * simplement ignoré, et le statut s'affichait sans aucune couleur.
 * ⚠️ **La garde des couleurs en dur ne pouvait pas le voir** : elle vérifie
 * l'ABSENCE de littéraux, pas la PRÉSENCE de couleurs qui fonctionnent. Un
 * jeton dans un `style` natif passe les deux contrôles et ne peint rien.
 *
 * ── POURQUOI UNE PASTILLE ET NON UN CHIP ───────────────────────────────────
 * Un `<Chip color="warning">` peint un aplat saturé qui pèse autant qu'un
 * bouton, sur chaque ligne d'un tableau. **La couleur est l'information, le
 * fond ne l'est pas** : un point coloré la porte aussi bien, et laisse le
 * texte se lire en couleur de texte normale. C'est ce qui fait qu'un tableau
 * de trente lignes cesse de clignoter.
 *
 * ⚠️ ET LE POINT SEUL NE SUFFIT PAS : la couleur ne doit jamais être le seul
 * porteur d'une information — le libellé est toujours écrit à côté.
 */
const PastilleStatut = ({ statut, famille = 'commande' }) => {
  const { t } = useLanguage();
  // ⚠️ UNE SEULE PROP DE PLUS, et elle nomme une distinction RÉELLE du domaine
  // — pas une variante d'affichage. Deux composants jumeaux auraient dupliqué
  // le rendu, c'est-à-dire recréé exactement l'incohérence qu'on corrige.
  const meta = (FAMILLES[famille] || FAMILLES.commande)[statut];

  // Un statut inconnu s'affiche tel quel, en gris : mieux vaut un code brut
  // lisible qu'une pastille vide qui laisserait croire à une absence.
  const couleur = meta ? `${meta.colorKey}.main` : 'text.disabled';
  const libelle = meta ? t(meta.i18nKey) : statut;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <Box
        component="span"
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: couleur,
          flexShrink: 0,
        }}
      />
      <Typography variant="body2" component="span" noWrap>
        {libelle}
      </Typography>
    </Box>
  );
};

export default PastilleStatut;
